import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import SimpleTextInput from '../../common/SimpleTextInput'
import Dropdown from '../../common/Dropdown'
import { toast } from '../../common/Toast'
import TooltipedIcon from '../../common/TooltipedIcon'
import { InformationIcon } from '../../../icons'
import { productModelsWritePermissions } from '../../../utils/permissionValidation'
import { clearProductModelsMessages, editAdminProductTypes, createAdminProductTypes } from '../../../actions/productModels'
import '../uploads.scss'

const boolFromAnything = (v) => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'string') return ['true', '1', 'y', 'yes'].includes(v.toLowerCase())
  if (typeof v === 'number') return v === 1
  return false
}

const normalizeProductTypePayload = (data = {}) => ({
  productType: (data.productType || '').trim(),
  categoryId: data.category?.id ?? '',
  brandId: data.brand?.id ?? '',
  deviceFunctionId: data.deviceFunction?.id ?? '',
  status: boolFromAnything(data.isActive) ? 'active' : 'inactive',
})

const AddEditProductType = (props) => {
  const { t } = useTranslation()
  const { typeDetails: incoming, closeModal, showSpinner, setShowSpinner } = props
  const isEditMode = !!incoming?.id
  const [productType, setProductType] = useState(incoming?.productType || '')
  const [categoryId, setCategoryId] = useState(incoming?.category?.id ?? '')
  const [brandId, setBrandId] = useState(incoming?.brand?.id ?? '')
  const [deviceFunctionId, setDeviceFunctionId] = useState(incoming?.deviceFunction?.id ?? '')
  const [status, setStatus] = useState(boolFromAnything(incoming?.isActive) ? 'active' : 'inactive')
  const [haveError, setHaveError] = useState({})
  const [saving, setSaving] = useState(false)
  const [initialValues, setInitialValues] = useState(null)
  const STATUSES = ['active', 'inactive']

  useEffect(() => {
    if (isEditMode) {
      setProductType(incoming?.productType || '')
      setCategoryId(incoming?.category?.id ?? '')
      setBrandId(incoming?.brand?.id ?? '')
      setDeviceFunctionId(incoming?.deviceFunction?.id ?? '')
      setStatus(boolFromAnything(incoming?.isActive) ? 'active' : 'inactive')
      setInitialValues(normalizeProductTypePayload(incoming))
      return
    }
    setProductType('')
    setCategoryId('')
    setBrandId('')
    setDeviceFunctionId('')
    setStatus('inactive')
    setInitialValues(null)
  }, [isEditMode, incoming?.id])

  const isDirty = useMemo(() => {
    if (!isEditMode || !initialValues) return true
    const current = {
      productType: productType.trim(),
      categoryId,
      brandId,
      deviceFunctionId,
      status,
    }
    return JSON.stringify(current) !== JSON.stringify(initialValues)
  }, [isEditMode, initialValues, productType, categoryId, brandId, status, deviceFunctionId])

  const deviceFunctionOptions = useMemo(() => {
    return (props.deviceFunctions || [])
      .map((df) => ({
        key: df?.id ?? '',
        value: df?.deviceFunction ?? df?.name ?? '',
      }))
      .filter((o) => o.key)
  }, [props.deviceFunctions])

  const brandOptions = useMemo(() => {
    return (props.brands || [])
      .map((b) => ({
        key: b?.id ?? '',
        value: b?.brand ?? b?.name ?? '',
      }))
      .filter((o) => o.key)
  }, [props.brands])

  const categoryOptions = useMemo(() => {
    return (props.productCategories || [])
      .map((pc) => ({
        key: pc?.id ?? '',
        value: pc?.category ?? pc?.name ?? '',
      }))
      .filter((o) => o.key)
  }, [props.productCategories])

  const categoryLabel = useMemo(() => {
    return categoryOptions.find((o) => o.key === categoryId)?.value || incoming?.category?.name || ''
  }, [categoryOptions, categoryId, incoming?.category?.name])

  const brandLabel = useMemo(() => {
    return brandOptions.find((o) => o.key === brandId)?.value || incoming?.brand?.name || ''
  }, [brandOptions, brandId, incoming?.brand?.name])

  const deviceFunctionLabel = useMemo(() => {
    return deviceFunctionOptions.find((o) => o.key === deviceFunctionId)?.value || incoming?.deviceFunction?.name || ''
  }, [deviceFunctionOptions, deviceFunctionId, incoming?.deviceFunction?.name])

  useEffect(() => {
    if (isEditMode) {
      setProductType(incoming?.productType || '')
      setCategoryId(incoming?.category?.id ?? '')
      setBrandId(incoming?.brand?.id ?? '')
      setStatus(boolFromAnything(incoming?.isActive) ? 'active' : 'inactive')
      setDeviceFunctionId(incoming?.deviceFunction?.id ?? '')
      return
    }
    setProductType('')
    setCategoryId('')
    setBrandId('')
    setStatus('inactive')
    setDeviceFunctionId('')
  }, [isEditMode, incoming?.id])

  const isValid = () => {
    let errors = {}
    let ok = true
    if (!productType.trim()) {
      errors.productType = true
      ok = false
    }
    if (!categoryId) {
      errors.categoryId = true
      ok = false
    }
    if (!deviceFunctionId) {
      errors.deviceFunctionId = true
      ok = false
    }
    if (!brandId) {
      errors.brandId = true
      ok = false
    }
    if (!status) {
      errors.status = true
      ok = false
    }
    setHaveError(errors)
    return ok
  }

  const handleSave = async () => {
    if (!isValid()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    setSaving(true)
    setShowSpinner?.(true)
    props.actions.clearProductModelsMessages?.()

    const payload = {
      productType: productType.trim(),
      devicesCategoriesId: categoryId,
      brandId: brandId,
      devicesFunctionId: deviceFunctionId,
      isActive: status === 'active',
    }

    try {
      const typeId = incoming?.id
      if (isEditMode) {
        await props.actions.editAdminProductTypes(typeId, payload)
      } else {
        await props.actions.createAdminProductTypes(payload)
      }
      closeModal()
    } finally {
      setSaving(false)
      setShowSpinner?.(false)
      props.actions.clearProductModelsMessages?.()
    }
  }

  const headerContent = () => (
    <div className="status-wrapper">
      <div className="label d-flex flex-justify-start flex-align-center">
        <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.type_status_info')}`} orientation="bottom" />
        {t('status')}
      </div>
      <Dropdown
        className="dropdown-input"
        options={STATUSES.map((s) => ({
          key: s,
          value: t(s),
        }))}
        initialSelectedKey={status}
        onChange={(opt) => setStatus(opt.key)}
        hasError={haveError.status}
      />
    </div>
  )

  return (
    <PageModal
      toggle
      onToggle={closeModal}
      className="uploads-modal product-types-modal"
      title={isEditMode ? t('product_models_context.edit_product_type') : t('product_models_context.add_product_type')}
      headerContent={headerContent()}
      footerContent={
        <>
          <SimpleButton className="cancel-button" onClick={closeModal}>
            {t('cancel')}
          </SimpleButton>
          <SimpleButton
            className="submit-button"
            onClick={handleSave}
            disabled={saving || !!showSpinner || (isEditMode && !isDirty)}
            requiredPermission={productModelsWritePermissions}
          >
            {saving ? t('saving') : t('save')}
          </SimpleButton>
        </>
      }
    >
      <div className="uploads-modal-content">
        <div className="section-title">{t('product_models_context.general_model_details').toUpperCase()}</div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('product_type')}*`}</p>
            <SimpleTextInput value={productType} onChange={setProductType} onClear={() => setProductType('')} hasError={haveError.productType} />
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('category')}*`}</p>
            {categoryOptions.length ? (
              <Dropdown
                placeholder={t('please_select')}
                options={categoryOptions}
                initialSelectedKey={categoryId || undefined}
                onChange={(opt) => setCategoryId(opt.key)}
                hasError={haveError.categoryId}
              />
            ) : (
              <SimpleTextInput value={categoryLabel} disabled={true} hasError={haveError.categoryId} />
            )}
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('product_models_context.device_function')}*`}</p>
            {deviceFunctionOptions.length ? (
              <Dropdown
                placeholder={t('please_select')}
                options={deviceFunctionOptions}
                initialSelectedKey={deviceFunctionId || undefined}
                onChange={(opt) => setDeviceFunctionId(opt.key)}
                hasError={haveError.deviceFunctionId}
              />
            ) : (
              <SimpleTextInput value={deviceFunctionLabel} disabled={true} hasError={haveError.deviceFunctionId} />
            )}
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('dealer-portal.brand')}*`}</p>
            {brandOptions.length ? (
              <Dropdown
                placeholder={t('please_select')}
                options={brandOptions}
                initialSelectedKey={brandId || undefined}
                onChange={(opt) => setBrandId(opt.key)}
                hasError={haveError.brandId}
              />
            ) : (
              <SimpleTextInput value={brandLabel} disabled={true} hasError={haveError.brandId} />
            )}
          </div>
        </div>
      </div>
    </PageModal>
  )
}

function stateToProps({ productModels, userPermissions }) {
  return {
    userPermissions,
    productTypes: productModels?.productModelsFilterOptions?.productTypes || [],
    productCategories: productModels?.productModelsFilterOptions?.productCategories || [],
    deviceFunctions: productModels?.productModelsFilterOptions?.deviceFunctions || [],
    technologies: productModels?.productModelsFilterOptions?.technologies || [],
    brands: productModels?.productModelsFilterOptions?.brands || [],
    adminProductTypes: productModels?.productTypes || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createAdminProductTypes,
        editAdminProductTypes,
        clearProductModelsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditProductType)

